import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Globe, BookOpen, Clock, Users, ChevronDown, ChevronUp, Info, Menu, X, Heart } from 'lucide-react';

const CourseTopics = () => {
  const [language, setLanguage] = useState<'pl' | 'en'>('pl');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedCourse, setExpandedCourse] = useState<string | null>(null);

  const content = {
    pl: {
      title: 'Tematy Kursów',
      subtitle: 'Szczegółowy program zajęć dla każdego kursu',
      disclaimer: {
        title: 'Tematyka kursu:',
        text: 'Poniższy program ma charakter orientacyjny. Ostateczny zakres materiału zostanie dostosowany do indywidualnych potrzeb uczestników danej grupy.'
      },
      additionalInfo: {
        text: 'Program naszych kursów to owoc lat doświadczeń, tysięcy godzin pracy z młodzieżą oraz głębokiej analizy tego, co realnie decyduje o sukcesie na ostatniej prostej przed maturą IB. Stworzylimy go z potrzeby serca i pełnego zrozumienia presji, z jaką mierzą się uczniowie, dlatego oferujemy **autorskie materiały naszych ekspertów**, których nie znajdziecie w żadnym podręczniku. Skupiamy się na precyzyjnym wskazaniu egzaminacyjnych wyzwań i pułapek, aby lipcowe wyniki dały Wam pełną satysfakcję i otworzyły drzwi do wymarzonych studiów, dalszego rozwoju oraz trwałych, międzynarodowych przyjazni! 🎓🌍'
      },
      courses: {
        biology_sl: {
          title: 'Biology SL',
          instructor: 'dr David O\'Keeffe',
          dates: '2-4.02.2026',
          sessions: [
            { number: 1, title: 'PODSTAWY MOLEKULARNE ŻYCIA', duration: '1.5h' },
            { number: 2, title: 'KOMÓRKI - STRUKTURA, BŁONY I TRANSPORT', duration: '1.5h' },
            { number: 3, title: 'METABOLIZM - ENZYMY I ENERGIA', duration: '1.5h' },
            { number: 4, title: 'PODZIAŁ KOMÓRKI I CIĄGŁOŚĆ', duration: '1.5h' },
            { number: 5, title: 'GENETYKA MOLEKULARNA', duration: '1.5h' },
            { number: 6, title: 'DZIEDZICZENIE I GENETYKA', duration: '1.5h' },
            { number: 7, title: 'EWOLUCJA I KLASYFIKACJA', duration: '1.5h' },
            { number: 8, title: 'INTEGRACJA SYSTEMÓW CIAŁA', duration: '1.5h' },
            { number: 9, title: 'KOORDYNACJA I KONTROLA', duration: '1.5h' },
            { number: 10, title: 'EKOLOGIA I EKOSYSTEMY', duration: '1.5h' }
          ]
        },
        biology_hl: {
          title: 'Biology HL',
          instructor: 'dr David O\'Keeffe',
          dates: '4-6.02.2026',
          sessions: [
            { number: 1, title: 'PODSTAWY MOLEKULARNE I ZAAWANSOWANA BIOCHEMIA', duration: '1.5h' },
            { number: 2, title: 'STRUKTURA KOMÓRKI, BŁONY I ZAAWANSOWANY TRANSPORT', duration: '1.5h' },
            { number: 3, title: 'METABOLIZM - SZCZEGÓŁOWE SZLAKI', duration: '1.5h' },
            { number: 4, title: 'PODZIAŁ KOMÓRKI I SZCZEGÓŁY MEJOZY', duration: '1.5h' },
            { number: 5, title: 'GENETYKA MOLEKULARNA I EKSPRESJA GENÓW', duration: '1.5h' },
            { number: 6, title: 'DZIEDZICZENIE, GENETYKA POPULACYJNA I BIOTECHNOLOGIA', duration: '1.5h' },
            { number: 7, title: 'EWOLUCJA, SPECJACJA I FILOGENETYKA', duration: '1.5h' },
            { number: 8, title: 'ZINTEGROWANE SYSTEMY CIAŁA - ZAAWANSOWANA FIZJOLOGIA', duration: '1.5h' },
            { number: 9, title: 'KONTROLA NERWOWA, HORMONY I WYDALANIE', duration: '1.5h' },
            { number: 10, title: 'EKOLOGIA, POPULACJE I ANALIZA STATYSTYCZNA', duration: '1.5h' }
          ]
        },
        mathematics_sl: {
          title: 'Mathematics AA SL',
          instructor: 'dr Bill Roberts',
          dates: '9-11.02.2026',
          sessions: [
            { number: 1, title: 'FUNKCJE, KRZYWE I KALKULATOR', duration: '1.5h' },
            { number: 2, title: 'RÓŻNICZKOWANIE', duration: '1.5h' },
            { number: 3, title: 'ZASTOSOWANIA TRYGONOMETRII', duration: '1.5h' },
            { number: 4, title: 'TWIERDZENIE DWUMIANOWE I LOGARYTMY', duration: '1.5h' },
            { number: 5, title: 'CAŁKOWANIE', duration: '1.5h' },
            { number: 6, title: 'CIĄGI ARYTMETYCZNE I GEOMETRYCZNE', duration: '1.5h' },
            { number: 7, title: 'WZORY TRYGONOMETRYCZNE, TOŻSAMOŚCI I RÓWNANIA', duration: '1.5h' },
            { number: 8, title: 'STATYSTYKA', duration: '1.5h' },
            { number: 9, title: 'PRAWDOPODOBIEŃSTWO I ROZKŁADY', duration: '1.5h' },
            { number: 10, title: 'ZASTOSOWANIA ANALIZY MATEMATYCZNEJ', duration: '1.5h' }
          ]
        },
        mathematics_hl: {
          title: 'Mathematics AA HL',
          instructor: 'dr Bill Roberts',
          dates: '11-13.02.2026',
          sessions: [
            { number: 1, title: 'ZASTOSOWANIA TRYGONOMETRII, WZORY I RÓWNANIA TRYGONOMETRYCZNE', duration: '1.5h' },
            { number: 2, title: 'FUNKCJE, KRZYWE I KALKULATOR', duration: '1.5h' },
            { number: 3, title: 'RÓŻNICZKOWANIE I ZASTOSOWANIA RÓŻNICZKOWANIA', duration: '1.5h' },
            { number: 4, title: 'CIĄGI ARYTMETYCZNE I GEOMETRYCZNE, TWIERDZENIE DWUMIANOWE I LOGARYTMY', duration: '1.5h' },
            { number: 5, title: 'CAŁKOWANIE', duration: '1.5h' },
            { number: 6, title: 'ZASTOSOWANIA CAŁKOWANIA WRAZ Z RÓWNANIAMI RÓŻNICZKOWYMI', duration: '1.5h' },
            { number: 7, title: 'LICZBY ZESPOLONE', duration: '1.5h' },
            { number: 8, title: 'WEKTORY', duration: '1.5h' },
            { number: 9, title: 'PRAWDOPODOBIEŃSTWO I ROZKŁADY', duration: '1.5h' },
            { number: 10, title: 'DOWODY I LICZBY ZESPOLONE', duration: '1.5h' }
          ]
        }
      }
    },
    en: {
      title: 'Course Topics',
      subtitle: 'Detailed curriculum for each course',
      disclaimer: {
        title: 'Course Topics:',
        text: 'The following syllabus is provisional. The final scope of work will be tailored to meet the specific needs of the students in each group.'
      },
      additionalInfo: {
        text: 'Our program is the result of years of expertise, thousands of hours spent with students, and a deep analysis of what truly determines success in the final stretch before the IB exams. Created with heart and a profound understanding of the pressure students face, we offer **proprietary materials** that cannot be found in any textbook. We focus on precisely identifying exam challenges and pitfalls to ensure your May results provide full satisfaction, opening doors to your dream universities, lifelong knowledge, and lasting global friendships! 🎓🌍'
      },
      courses: {
        biology_sl: {
          title: 'Biology SL',
          instructor: 'Dr David O\'Keeffe',
          dates: '2-4.02.2026',
          sessions: [
            { number: 1, title: 'MOLECULAR FOUNDATIONS OF LIFE', duration: '1.5h' },
            { number: 2, title: 'CELLS - STRUCTURE, MEMBRANES, AND TRANSPORT', duration: '1.5h' },
            { number: 3, title: 'METABOLISM - ENZYMES AND ENERGY', duration: '1.5h' },
            { number: 4, title: 'CELL DIVISION AND CONTINUITY', duration: '1.5h' },
            { number: 5, title: 'MOLECULAR GENETICS', duration: '1.5h' },
            { number: 6, title: 'INHERITANCE AND GENETICS', duration: '1.5h' },
            { number: 7, title: 'EVOLUTION AND CLASSIFICATION', duration: '1.5h' },
            { number: 8, title: 'BODY SYSTEMS INTEGRATION', duration: '1.5h' },
            { number: 9, title: 'COORDINATION AND CONTROL', duration: '1.5h' },
            { number: 10, title: 'ECOLOGY AND ECOSYSTEMS', duration: '1.5h' }
          ]
        },
        biology_hl: {
          title: 'Biology HL',
          instructor: 'Dr David O\'Keeffe',
          dates: '4-6.02.2026',
          sessions: [
            { number: 1, title: 'MOLECULAR FOUNDATIONS AND ADVANCED BIOCHEMISTRY', duration: '1.5h' },
            { number: 2, title: 'CELL STRUCTURE, MEMBRANES, AND ADVANCED TRANSPORT', duration: '1.5h' },
            { number: 3, title: 'METABOLISM - DETAILED PATHWAYS', duration: '1.5h' },
            { number: 4, title: 'CELL DIVISION AND MEIOSIS DETAIL', duration: '1.5h' },
            { number: 5, title: 'MOLECULAR GENETICS AND GENE EXPRESSION', duration: '1.5h' },
            { number: 6, title: 'INHERITANCE, POPULATION GENETICS, AND BIOTECHNOLOGY', duration: '1.5h' },
            { number: 7, title: 'EVOLUTION, SPECIATION, AND PHYLOGENETICS', duration: '1.5h' },
            { number: 8, title: 'INTEGRATED BODY SYSTEMS - ADVANCED PHYSIOLOGY', duration: '1.5h' },
            { number: 9, title: 'NEURAL CONTROL, HORMONES, AND EXCRETION', duration: '1.5h' },
            { number: 10, title: 'ECOLOGY, POPULATIONS, AND STATISTICAL ANALYSIS', duration: '1.5h' }
          ]
        },
        mathematics_sl: {
          title: 'Mathematics AA SL',
          instructor: 'Dr Bill Roberts',
          dates: '9-11.02.2026',
          sessions: [
            { number: 1, title: 'FUNCTIONS, CURVES AND THE CALCULATOR', duration: '1.5h' },
            { number: 2, title: 'DIFFERENTIATION', duration: '1.5h' },
            { number: 3, title: 'APPLICATIONS OF TRIGONOMETRY', duration: '1.5h' },
            { number: 4, title: 'BINOMIAL THEOREM AND LOGARITHMS', duration: '1.5h' },
            { number: 5, title: 'INTEGRATION', duration: '1.5h' },
            { number: 6, title: 'ARITHMETIC SERIES, GEOMETRIC SERIES', duration: '1.5h' },
            { number: 7, title: 'TRIGONOMETRIC FORMULAS, IDENTITIES AND EQUATIONS', duration: '1.5h' },
            { number: 8, title: 'STATISTICS', duration: '1.5h' },
            { number: 9, title: 'PROBABILITY INCLUDING DISTRIBUTIONS', duration: '1.5h' },
            { number: 10, title: 'APPLICATIONS OF CALCULUS', duration: '1.5h' }
          ]
        },
        mathematics_hl: {
          title: 'Mathematics AA HL',
          instructor: 'Dr Bill Roberts',
          dates: '11-13.02.2026',
          sessions: [
            { number: 1, title: 'APPLICATIONS OF TRIGONOMETRY, TRIGONOMETRIC FORMULAS, IDENTITIES AND EQUATIONS', duration: '1.5h' },
            { number: 2, title: 'FUNCTIONS, CURVES AND THE CALCULATOR', duration: '1.5h' },
            { number: 3, title: 'DIFFERENTIATION AND APPLICATIONS OF DIFFERENTIATION', duration: '1.5h' },
            { number: 4, title: 'ARITHMETIC SERIES, GEOMETRIC SERIES, BINOMIAL THEOREM AND LOGARITHMS', duration: '1.5h' },
            { number: 5, title: 'INTEGRATION', duration: '1.5h' },
            { number: 6, title: 'APPLICATIONS OF INTEGRATION INCLUDING DIFFERENTIAL EQUATIONS', duration: '1.5h' },
            { number: 7, title: 'COMPLEX NUMBERS', duration: '1.5h' },
            { number: 8, title: 'VECTORS', duration: '1.5h' },
            { number: 9, title: 'PROBABILITY INCLUDING DISTRIBUTIONS', duration: '1.5h' },
            { number: 10, title: 'PROOF AND COMPLEX NUMBERS', duration: '1.5h' }
          ]
        }
      }
    }
  };

  const t = content[language];

  const toggleCourse = (courseId: string) => {
    setExpandedCourse(expandedCourse === courseId ? null : courseId);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-yellow-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">IB</span>
            </div>
            <span className="font-bold text-xl">DP Accelerator</span>
          </Link>
          
          <div className="hidden md:flex space-x-6">
            <Link to="/" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Strona główna' : 'Home'}</Link>
            <Link to="/experts" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Eksperci' : 'Experts'}</Link>
            <Link to="/schedule" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Harmonogram' : 'Schedule'}</Link>
            <Link to="/course-topics" className="text-blue-600 font-semibold">{language === 'pl' ? 'Tematy' : 'Topics'}</Link>
            <Link to="/pricing" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Dołącz do kursu' : 'Join the DP Accelerator'}</Link>
            <Link to="/code-of-conduct" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Regulamin' : 'Code of Conduct'}</Link>
            <Link to="/contact" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Kontakt' : 'Contact'}</Link>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}
              className="flex items-center space-x-1"
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'pl' ? 'EN' : 'PL'}</span>
            </Button>
            
            {/* Mobile menu button */}
            <Button
              variant="outline"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b shadow-lg">
            <div className="container mx-auto px-4 py-4 space-y-4">
              <Link 
                to="/" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Strona główna' : 'Home'}
              </Link>
              <Link 
                to="/experts" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Eksperci' : 'Experts'}
              </Link>
              <Link 
                to="/schedule" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Harmonogram' : 'Schedule'}
              </Link>
              <Link 
                to="/course-topics" 
                className="block text-blue-600 font-semibold"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Tematy' : 'Topics'}
              </Link>
              <Link 
                to="/pricing" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Dołącz do kursu' : 'Join the DP Accelerator'}
              </Link>
              <Link 
                to="/code-of-conduct" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Regulamin' : 'Code of Conduct'}
              </Link>
              <Link 
                to="/contact" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Kontakt' : 'Contact'}
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Header */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-yellow-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-yellow-200 bg-clip-text text-transparent">
            {t.title}
          </h1>
          <p className="text-xl md:text-2xl text-blue-100">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-amber-50 border-b border-amber-200">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-amber-100 border-amber-300">
              <CardContent className="p-6">
                <div className="flex items-start space-x-3">
                  <Info className="w-6 h-6 text-amber-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-amber-900 mb-2">{t.disclaimer.title}</h3>
                    <p className="text-amber-800">{t.disclaimer.text}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Additional Info */}
            <Card className="bg-blue-50 border-blue-200 mt-6">
              <CardContent className="p-6">
                <div className="flex items-start space-x-3">
                  <Heart className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-blue-800 leading-relaxed">
                      {t.additionalInfo.text.split('**').map((part, index) => 
                        index % 2 === 1 ? <strong key={index}>{part}</strong> : part
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Course Topics */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto space-y-8">
            
            {/* Biology Courses */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
                {language === 'pl' ? 'Biologia' : 'Biology'}
              </h2>
              
              {['biology_sl', 'biology_hl'].map((courseId) => {
                const course = t.courses[courseId];
                const isExpanded = expandedCourse === courseId;
                
                return (
                  <Card key={courseId} className="hover:shadow-lg transition-shadow">
                    <CardHeader 
                      className="cursor-pointer"
                      onClick={() => toggleCourse(courseId)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                            <BookOpen className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <CardTitle className="text-2xl">{course.title}</CardTitle>
                            <CardDescription className="flex items-center space-x-4 mt-1">
                              <span>{course.instructor}</span>
                              <span>•</span>
                              <span>{course.dates}</span>
                              <span>•</span>
                              <span className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                10 {language === 'pl' ? 'sesji' : 'sessions'}
                              </span>
                            </CardDescription>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm">
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </Button>
                      </div>
                    </CardHeader>
                    
                    {isExpanded && (
                      <CardContent>
                        <div className="grid md:grid-cols-2 gap-4">
                          {course.sessions.map((session, index) => (
                            <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                                {session.number}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-sm">{session.title}</p>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {session.duration}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>

            {/* Mathematics Courses */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
                {language === 'pl' ? 'Matematyka' : 'Mathematics'}
              </h2>
              
              {['mathematics_sl', 'mathematics_hl'].map((courseId) => {
                const course = t.courses[courseId];
                const isExpanded = expandedCourse === courseId;
                
                return (
                  <Card key={courseId} className="hover:shadow-lg transition-shadow">
                    <CardHeader 
                      className="cursor-pointer"
                      onClick={() => toggleCourse(courseId)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <BookOpen className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <CardTitle className="text-2xl">{course.title}</CardTitle>
                            <CardDescription className="flex items-center space-x-4 mt-1">
                              <span>{course.instructor}</span>
                              <span>•</span>
                              <span>{course.dates}</span>
                              <span>•</span>
                              <span className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                10 {language === 'pl' ? 'sesji' : 'sessions'}
                              </span>
                            </CardDescription>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm">
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </Button>
                      </div>
                    </CardHeader>
                    
                    {isExpanded && (
                      <CardContent>
                        <div className="grid md:grid-cols-2 gap-4">
                          {course.sessions.map((session, index) => (
                            <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                                {session.number}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-sm">{session.title}</p>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {session.duration}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>

            {/* Geography Courses */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
                {language === 'pl' ? 'Geografia' : 'Geography'}
              </h2>
              
              {/* Geography SL */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl">Geography SL</CardTitle>
                        <CardDescription className="flex items-center space-x-4 mt-1">
                          <span>mgr Anna Krzemińska-Kaczyńska</span>
                          <span>•</span>
                          <span>2.02 (13:30) - 4.02.2026</span>
                          <span>•</span>
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            10 {language === 'pl' ? 'sesji' : 'sessions'}
                          </span>
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-green-50 p-6 rounded-lg">
                    <h3 className="text-lg font-bold text-green-900 mb-3">
                      {language === 'pl' ? 'Geografia IB: Strategia i Precyzja' : 'IB Geography: Strategy & Precision'}
                    </h3>
                    <p className="text-green-800 leading-relaxed">
                      {language === 'pl' 
                        ? 'Dołącz do kursu opartego na autorskich materiałach, które łączą tematy Core i Options w spójną całość. nauczysz się, jak konstruować precyzyjne odpowiedzi, których szukają egzaminatorzy. Zainwestuj w swój wynik – zapraszam!'
                        : 'Join this intensive course based on unique, proprietary materials designed to bridge the gap between Core and Optional themes. Focusing on teaching you how to deliver precise, high-scoring exam answers. Take the next step toward your IB success – feel invited!'}
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              {/* Geography HL */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl">Geography HL</CardTitle>
                        <CardDescription className="flex items-center space-x-4 mt-1">
                          <span>mgr Anna Krzemińska-Kaczyńska</span>
                          <span>•</span>
                          <span>5-6.02.2026</span>
                          <span>•</span>
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            10 {language === 'pl' ? 'sesji' : 'sessions'}
                          </span>
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-green-50 p-6 rounded-lg">
                    <h3 className="text-lg font-bold text-green-900 mb-3">
                      {language === 'pl' ? 'Geografia IB: Strategia i Precyzja' : 'IB Geography: Strategy & Precision'}
                    </h3>
                    <p className="text-green-800 leading-relaxed mb-4">
                      {language === 'pl' 
                        ? ''
                        : 'Join this intensive course based on unique, proprietary materials designed to bridge the gap between Core and Optional themes. Focusing on teaching you how to deliver precise, high-scoring exam answers.'}
                    </p>
                    <div className="bg-green-100 p-4 rounded-lg border-l-4 border-green-600">
                      <p className="text-green-900 font-semibold">
                        {language === 'pl' ? 'Specjalnie dla HL:' : 'Special for HL:'}
                      </p>
                      <p className="text-green-800">
                        {language === 'pl' 
                          ? 'Uczniowie HL skupią się na analizie wyselekcjonowanych case studies, które pozwolą zdobyć maksymalną liczbę punktów w eseju. Zainwestuj w swój wynik – zapraszam!'
                          : 'HL students will master the best-performing case studies to maximize their essay scores. Take the next step toward your IB success – feel invited!'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <Link to="/" className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-yellow-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">IB</span>
              </div>
              <span className="font-bold text-xl">DP Accelerator</span>
            </Link>
            <p className="text-gray-400">
              {language === 'pl' 
                ? 'Intensywne kursy przygotowawcze do matury międzynarodowej IB.' 
                : 'Intensive preparation courses for the International Baccalaureate.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CourseTopics;